/**
 * HTML utility functions for safe DOM manipulation
 * Prevents XSS attacks by escaping user-controlled content
 *
 * Usage:
 * - ES Modules: import { escapeHtml } from './utils/html-utils.js';
 * - Traditional scripts: window.escapeHtml(text) or just escapeHtml(text)
 */

function escapeHtml(text) {
  if (text === null || text === undefined) {
    return '';
  }
  const str = String(text);
  const htmlEscapes = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  };
  return str.replace(/[&<>"']/g, (char) => htmlEscapes[char]);
}

if (typeof window !== 'undefined') {
  window.escapeHtml = escapeHtml;
  window.safeSetInnerHTML = safeSetInnerHTML;
  window.safeInsertHTML = safeInsertHTML;
}

export { escapeHtml, safeSetInnerHTML, safeInsertHTML };

/**
 * Safely set innerHTML by wrapping content with escapeHtml
 * This function is recognized by ESLint's no-unsanitized plugin
 *
 * @param {HTMLElement} element - The element to modify
 * @param {string} htmlString - HTML string (will be used as-is, ensure it's already escaped or static)
 */
function safeSetInnerHTML(element, htmlString) {
  element.innerHTML = escapeHtml(htmlString);
}

/**
 * Safely insert HTML using insertAdjacentHTML
 *
 * @param {HTMLElement} element - The element to insert into
 * @param {string} position - Position: 'beforebegin', 'afterbegin', 'beforeend', 'afterend'
 * @param {string} htmlString - HTML string (will be used as-is)
 */
function safeInsertHTML(element, position, htmlString) {
  element.insertAdjacentHTML(position, escapeHtml(htmlString));
}
